import Link from 'next/link';
import styles from './index.module.less';
import { AppSettings, PageType } from '@/shared/app-common';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import Language from '@/components/Common/Language';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';

const GlobalFooter = ({ show = true, className }: { show?: boolean; className?: string; pageType?: PageType }) => {
  const { t } = useTranslation('common');

  if (!show) {
    return <></>;
  }

  const cls = classNames(styles.footer, className);

  return (
    <footer className={cls}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <div className={styles.logo}>
            <div style={{ display: 'none' }}>{t('Erasa')}</div>
            <div className={styles.logoIcon}>
              <Image
                src={'/images/layouts/erasa.webp'}
                alt={AppSettings.name}
                loading="lazy"
                width={160}
                height={50}
                style={{ width: 'auto' }}
              />
            </div>
          </div>

          <p style={{ marginTop: '20px' }}>{t('The All-In-One Platform To Protect Your Digital Presence')}</p>

          <div style={{ marginTop: '20px' }}>
            <Language />
          </div>

          <div className={styles.socialContainer}>
            <p>{t('Follow us')}</p>
            <div className={styles.socialLinks}>
              <Link href="https://twitter.com/Erasaprotect" title="Erasa X account" target="__block">
                <Image src="/images/layouts/x-link-icon.webp" alt="x" width={32} height={32} loading="lazy" />
              </Link>
              <Link
                href="https://www.facebook.com/profile.php?id=61565526387773"
                title="Erasa Facebook Page"
                target="__block"
              >
                <Image src="/images/layouts/fb-link-icon.webp" alt="facebook" width={32} height={32} loading="lazy" />
              </Link>
            </div>
          </div>
        </div>

        <div className={styles.linksGrid}>
          <div className={`${styles.linkColumn} ${styles.servicesColumn}`}>
            <div style={{ fontWeight: 'bold' }}>{t('Services').toLocaleUpperCase()}</div>
            <ul>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/remove-leaked-onlyfans-content`}
                  title={t('OnlyFans Content Protection')}
                >
                  {t('OnlyFans Content Protection')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/remove-fake-account`}
                  title={t('Impersonation Account Removal')}
                >
                  {t('Impersonation Account Removal')}
                </Link>
              </li>
              <li>
                <Link href={`${process.env.NEXT_APP_WEB_HOST}/cam-model-protection`} title={t('Cam Model Protection')}>
                  {t('Cam Model Protection')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/leaked-private-photos`}
                  title={t('Private Photo Protection')}
                >
                  {t('Private Photo Protection')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/find-and-remove-revenge-porn`}
                  title={t('Revenge Porn Removal')}
                >
                  {t('Revenge Porn Removal')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/ai-porn-detection-removal`}
                  title={t('AI Image Abuse Detection')}
                >
                  {t('AI Image Abuse Detection')}
                </Link>
              </li>
            </ul>
          </div>

          <div className={`${styles.linkColumn} ${styles.toolsColumn}`}>
            <div style={{ fontWeight: 'bold' }}>{t('Free Tools').toLocaleUpperCase()}</div>
            <ul>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/shadowban-test/twitter-shadowban-test`}
                  title={t('Twitter Shadowban Test')}
                >
                  {t('Twitter Shadowban Test')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/shadowban-test/instagram-shadowban-test`}
                  title={t('Instagram Shadowban Test')}
                >
                  {t('Instagram Shadowban Test')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/shadowban-test/tiktok-shadowban-test`}
                  title={t('Tiktok Shadowban Test')}
                >
                  {t('Tiktok Shadowban Test')}
                </Link>
              </li>
              <li>
                <Link href={`${process.env.NEXT_APP_WEB_HOST}/compare`} title={t('Creator Platform Comparison Tool')}>
                  {t('Creator Platform Comparison Tool')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/onlyfans-restricted-words-checker`}
                  title={t('OnlyFans Restricted Words Checker')}
                >
                  {t('OnlyFans Restricted Words Checker')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/onlyfans-caption-generator`}
                  title={t('OnlyFans Caption Generator')}
                >
                  {t('OnlyFans Caption Generator')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/content-monitoring/reverse-username-search`}
                  title={t('Reverse Username Search')}
                >
                  {t('Reverse Username Search')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/content-monitoring/reverse-video-search`}
                  title={t('Reverse Video Search')}
                >
                  {t('Reverse Video Search')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/content-monitoring/reverse-photo-search`}
                  title={t('Reverse Photo Search')}
                >
                  {t('Reverse Photo Search')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${process.env.NEXT_APP_WEB_HOST}/content-monitoring/reverse-face-search`}
                  title={t('Reverse Face Search')}
                >
                  {t('Reverse Face Search')}
                </Link>
              </li>
            </ul>
          </div>

          <div className={`${styles.linkColumn} ${styles.resourcesColumn}`}>
            <div style={{ fontWeight: 'bold' }}>{t('Resources').toLocaleUpperCase()}</div>
            <ul>
              {
                <li>
                  <Link
                    href={`${process.env.NEXT_APP_WEB_HOST}/plan`}
                    title={t('Pricing')}
                    target="__block"
                    onClick={() => {
                      ga_logEvent(EventName.view_pricing);
                    }}
                  >
                    {t('Pricing')}
                  </Link>
                </li>
              }
              <li>
                <Link href={`${process.env.NEXT_APP_WEB_HOST}/blog`} title={t('blog')}>
                  {t('blog')}
                </Link>
              </li>
              <li>
                <Link href={`${process.env.NEXT_APP_WEB_HOST}/guide`} title={t('DMCA Guides')}>
                  {t('DMCA Guides')}
                </Link>
              </li>
              <li>
                <Link href={`${process.env.NEXT_APP_WEB_HOST}/dmca-protection-badge`} title={t('DMCA Badge')}>
                  {t('DMCA Badge')}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className={styles.bottomSection}>
        <p>All rights reserved. © 2026 Erasa</p>
        <div className={styles.legalLinks}>
          <Link href={`${process.env.NEXT_APP_WEB_HOST}/privacy-policy`} title={t('privacyPolicy')}>
            {t('privacyPolicy')}
          </Link>
          <Link href={`${process.env.NEXT_APP_WEB_HOST}/terms-us`} title={t('terms of us')}>
            {t('terms of us')}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default GlobalFooter;
